// src/pages/settings/forms/FormPage.tsx
import React, { useState, useEffect } from "react";
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { PlusOutlined } from "@ant-design/icons";
import { Drawer, Tag } from "antd";

import PageContainer from "../../../../components/shared/PageContainer";
import PageToolbar from "../../../../components/shared/PageToolbar";
import { DataTable } from "../../../../components/shared";

import { Form as FormType, FormElement, FormSection, defaultForm, fetchForms } from "../../../../api/formApi";
import { User } from "../../../../types/admin/UserTypes";
import UnifiedFormRenderer from "../../../../components/forms/FormRenderer";
import FormBuilderDrawer from "./FormBuilderDrawer";
import { hasFeature } from "../../../../utils/subscriptions/subscriptionService";

export const FormBuilder: React.FC<{ embedded?: boolean }> = () => {
	const user: User = getSessionUser();
	// Clinical (non-admin) staff get a read-only view; admins keep the full editor.
	const viewOnly = hasFeature("healthcare.peptide") && !hasFeature("healthcare");
	const [loading, setLoading] = useState(false);
	const [forms, setForms] = useState<FormType[]>([]);
	const [form, setForm] = useState<FormType | null>(null);
	const [search, setSearch] = useState("");
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [previewForm, setPreviewForm] = useState<FormType | null>(null);

	useEffect(() => {
		loadForms("");
	}, []);

	const loadForms = async (q: string) => {
		setLoading(true);
		try {
			const { data } = await fetchForms(q);
			const transformed = data.map((f: FormType) => ({
				...f,
				structure: typeof f.structure === "string" ? JSON.parse(f.structure) : f.structure,
			}));
			setForms(transformed);
		} finally {
			setLoading(false);
		}
	};

	const handleCreate = () => {
		setForm({ ...defaultForm, practitioner_id: user.id });
		setIsDrawerOpen(true);
	};

	const handleEdit = (f: FormType) => {
		// Prevent editing global forms
		if (f.is_global) {
			return;
		}
		setForm(f);
		setIsDrawerOpen(true);
	};

	const handleDrawerClose = () => {
		setForm(null);
		setIsDrawerOpen(false);
		loadForms("");
	};

	const columns: ColumnsType<FormType> = [
		{
			title: "Title",
			dataIndex: "title",
			key: "title",
			sorter: (a: FormType, b: FormType) => a.title.localeCompare(b.title),
			render: (text: string, record: FormType) => (
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<span>{text}</span>
					{record.is_global && (
						<Tag color="geekblue">Global</Tag>
					)}
				</div>
			),
		},
		{
			title: "Department",
			dataIndex: "department",
			key: "department",
			sorter: (a: FormType, b: FormType) => (a.department || "").localeCompare(b.department || ""),
		},
		{
			title: "Created",
			dataIndex: "created_at",
			key: "created_at",
			width: 130,
			sorter: true,
			render: (d: string) => (d ? dayjs(d).format("DD MMM YYYY") : "—"),
		},
	];

	return (
		<PageContainer>
			<PageToolbar
				primaryAction={viewOnly ? undefined : { label: "New Form", icon: <PlusOutlined />, onClick: handleCreate }}
				filters={[
					{
						type: "search",
						placeholder: "Search forms…",
						value: search,
						onChange: (v) => {
							setSearch(v);
							loadForms(v);
						},
						width: 260,
					},
				]}
				onReset={() => {
					setSearch("");
					loadForms("");
				}}
			/>
			<DataTable
				columns={columns}
				dataSource={forms}
				rowKey="id"
				loading={loading}
				onRowClick={(form) => {
					if (viewOnly) {
						// Read-only preview for every form (local and global)
						setPreviewForm(form);
						return;
					}
					// Only allow clicking non-global forms
					if (!form.is_global) {
						handleEdit(form);
					}
				}}
				emptyTitle="No forms yet"
				emptyDescription={viewOnly ? "No forms have been created yet" : "Create your first form to get started"}
				emptyAction={viewOnly ? undefined : { label: "New Form", onClick: handleCreate }}
			/>
			{!viewOnly && isDrawerOpen && form && (
				<FormBuilderDrawer
					form={form}
					isOpen={isDrawerOpen}
					isSubmitting={loading}
					setIsSubmitting={setLoading}
					onClose={handleDrawerClose}
				/>
			)}
			{viewOnly && previewForm && (
				<Drawer
					data-testid="forms-preview-drawer"
					title={previewForm.title}
					open={!!previewForm}
					width={720}
					onClose={() => setPreviewForm(null)}
				>
					<UnifiedFormRenderer
						structure={
							(typeof previewForm.structure === "string"
								? JSON.parse(previewForm.structure)
								: previewForm.structure) as (FormElement | FormSection)[]
						}
						useSections={!!previewForm.use_sections}
						values={{}}
						onValuesChange={() => {}}
						readOnly
						sectionMode="flat"
						title={previewForm.title}
					/>
				</Drawer>
			)}
		</PageContainer>
	);

};

export default FormBuilder;
			